import { Skeleton } from '@chakra-ui/react';
import { useMemo } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import {
  FundraisingStatus,
  useGetFundraisingQuery,
  useUpdateFundraisingMutation,
} from '@blockpulse3/graphql/hooks';

import { getPrivateFundraisingParameters } from '../../../utils';
import { IPrivateFundraisingParametersForm } from '../types';
import { NewPrivateFundraisingInformations } from './NewPrivateFundraisingInformations';

type Props = unknown;

/**
 * NewPrivateFundraisingParameters.
 * First step of fundraising funnel, parameters.
 *
 * @returns {JSX.Element}
 */
export function NewPrivateFundraisingParameters(): JSX.Element {
  const i18nFundraisingParametersForm = useTranslations('FundraisingParametersForm');

  const { fundraisingId = '', companyId = '' } = useParams();
  const navigate = useNavigate();

  const { data, loading, refetch } = useGetFundraisingQuery({
    variables: { fundraisingId },
    skip: !fundraisingId,
    onCompleted: (data) => {
      if (data.fundraising.status !== FundraisingStatus.DRAFT) {
        navigate(routes.workspace.fmt(companyId));
      }
    },
    onError: () => {
      navigate(routes.workspace.fmt(companyId));
    },
  });

  const [updateFundraising, { loading: updateLoading }] = useUpdateFundraisingMutation();

  const defaultValues = useMemo(() => {
    return getPrivateFundraisingParameters(data?.fundraising);
  }, [data]);

  const handleFormSubmit: SubmitHandler<IPrivateFundraisingParametersForm> = (data): void => {
    const selectedHoldingMethods = data.allowedHoldingMethods.map(
      (holdingMethod) => holdingMethod.value,
    );

    const privateFundrainsingVariables = {
      fundraisingId,
      name: data.name,
      pricePerShare: data.pricePerShare,
      subscriptionPeriod: data.subscriptionPeriod.value,
      assetType: data.assetType,
      allowedHoldingMethods: selectedHoldingMethods,
    };

    updateFundraising({
      variables: {
        updateFundraisingInput: privateFundrainsingVariables,
      },
      onCompleted: () => {
        if (companyId) {
          navigate(
            routes.workspace.fundraising.private.documentation.fmt(companyId, fundraisingId),
          );
          refetch();
        }
      },
    });
  };
  return (
    <Skeleton isLoaded={!loading} w="full">
      <NewPrivateFundraisingInformations
        defaultValues={defaultValues}
        isLoading={loading || updateLoading}
        loadingText={i18nFundraisingParametersForm('UpdateLoading')}
        onSubmit={handleFormSubmit}
      />
    </Skeleton>
  );
}

export type NewPrivateFundraisingParametersProps = Props;
